import { useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import { WordMorphemeGlyphTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import {
  getMorphemeGlyphTileWordPinyinStyle,
  useBatchedWordTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";

/** The tile's `border` (see `morphemeGlyphTileVariants`), inside which the content is laid out. */
const TILE_BORDER_WIDTH = 1;

/**
 * Curriculum word-level grid. Tiles show each word's constituent pinyin readings (vocabulary is
 * introduced by audio), with one batched pinyin observer for the mounted word set.
 */
export function WordLevelGlyphGrid({
  columns = 8,
  onNavigate,
  tileSize,
  words,
}: {
  columns?: number;
  onNavigate: (word: string) => void;
  tileSize: number;
  words: readonly string[];
}) {
  const data = useBatchedWordTileData(words);
  const glyphStyles = useMemo(() => {
    const contentSize = Math.max(1, tileSize - TILE_BORDER_WIDTH * 2);
    return words.map((word) =>
      getMorphemeGlyphTileWordPinyinStyle({
        contentHeight: contentSize,
        contentWidth: contentSize,
        toneNumbersPinyinList: data.getSlice(word).primaryPinyin,
      }),
    );
  }, [data, tileSize, words]);
  return (
    <MorphemeTilePresentationProvider>
      <View className="gap-1 p-1">
        {Array.from({ length: Math.ceil(words.length / columns) }, (_, row) => (
          <View className="flex-row gap-1" key={row}>
            {Array.from({ length: columns }, (_, column) => {
              const index = row * columns + column;
              const word = words[index];
              return (
                <View className="flex-1 items-center justify-center" key={column}>
                  {word === undefined ? (
                    <View className="aspect-square w-full" />
                  ) : (
                    <WordMorphemeGlyphTileLink
                      front="pinyin"
                      glyphStyle={glyphStyles[index]}
                      href="/(nav)/(app)/browse/morpheme/[morpheme]"
                      morphemeGlyphTileSlice={data.getSlice(word)}
                      onNavigate={onNavigate}
                      word={word}
                    />
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </MorphemeTilePresentationProvider>
  );
}
